<template>
  <q-list
    ref="scrollTargetRef"
    :style="
      mHeight
        ? `max-height: ${mHeight}px; width: 100%; overflow-y: auto; overflow-x: hidden;`
        : mHeight > -1
        ? 'max-height: 45vh;  width: 100%; overflow-y: auto; overflow-x: hidden;'
        : 'height: calc(100% - 30px); width: 100%; overflow-y: auto; overflow-x: hidden;'
    "
    class="scroll"
  >
    <q-infinite-scroll
      ref="infscr"
      :debounce="100"
      @load="loadData"
      :offset="250"
      :scroll-target="scrollTargetRef"
    >
      <div
        class="row no-wrap items-start deps-select-all-row"
        :class="{ 'deps-select-all-row--deps': hasDeps, 'select-all-row--storages': isStoragesMode }"
        v-if="showSelectAll && allowMultiple && (hasDeps || !hideCheckboxes) && (!hasDeps || visibleDeps.length)"
      >
        <mosta-checkbox
          :class="['rec-checkbox', { 'q-mx-xs': !hasDeps && !isStoragesMode, 'select-all-check--storages': isStoragesMode }]"
          @click.stop.prevent
          :model-value="getSelectedAll"
          v-if="!hideCheckboxes"
          @update:model-value="setSelectedAll"
        />
        <div :class="['deps-select-all-label', { 'select-all-label--storages': isStoragesMode }]">{{ $t("all") }}</div>
      </div>

      <variable-list
        :is-table="isTableView"
        v-if="!hasDeps"
        ref="simple_selector_list"
        class="q-px-xs"
        :elements="data_list"
        v-model="listModelData"
        :allow-multiple="allowMultiple"
        :allow-wrap="true"
        :slot-wrap-display-contents="isTableView"
        :is-item-disabled="storageItemIsDisabled"
        @itemSelected="onItemSelected"
        :viewport="$refs.scrollTargetRef"
        :show-checkbox="allowMultiple && !hideCheckboxes"
        @emptyClick="onEmptyClick"
      >
        <template v-slot:default="slotProps">
          <slot name="item" :item="slotProps.item">
            <parameter-inline-display
              v-if="elementType === VariableType.ParameterInline"
              id="ds" :editable="false" :is-table="true"
              :model-value="getPositionNameModel(slotProps.item)"
            />
            <bill-row v-else-if="elementType === VariableType.Bill"
              :el="slotProps.item.bill" :bill-type="slotProps.item.billRowType" />
            <counterparty-row v-else-if="elementType === VariableType.Counterparty"
              :el="slotProps.item" />
            <simple-element
              v-else
              :element="slotProps.item"
              :elementType="elementType"
              :filter_string="objectFilter ? filter_value.stringFilter : filter_value"
            />
          </slot>
        </template>
      </variable-list>

      <div v-else>
        <div
          v-for="(dep, d_index) in visibleDeps"
          :key="dep.department + '_' + d_index"
          class="deps-block"
        >
          <div class="row no-wrap items-start deps-header-row">
            <mosta-checkbox
              v-if="!hideCheckboxes"
              class="rec-checkbox deps-header-check"
              @click.stop.prevent
              :model-value="getSelectedDepModel(d_index)"
              @update:model-value="setSelectedDepModel(d_index)"
            />
            <div class="deps-header-title">
              <span v-for="(span, idx) in getDepTitleSpans(dep.department)" :key="`${dep.department}_${idx}`" :style="span.style">
                {{ span.text }}
              </span>
            </div>
          </div>
          <variable-list
            :class="['deps-children-list', { 'deps-children-list--full': full }]"
            :ref="(el) => { divs[d_index] = el; }"
            :elements="dep.child"
            :columns="full ? 2 : 1"
            v-model="listModelData"
            :allow-multiple="allowMultiple"
            :allow-wrap="true"
            @itemSelected="onItemSelected"
            :viewport="$refs.scrollTargetRef"
            :show-checkbox="hasDeps && !hideCheckboxes"
            @emptyClick="onEmptyClick"
          >
            <template v-slot:default="slotProps">
              <slot name="item" :item="slotProps.item">
                <parameter-inline-display
                  v-if="elementType === VariableType.ParameterInline"
                  id="ds" :editable="false" :is-table="true"
                  :model-value="getPositionNameModel(slotProps.item)"
                />
                <bill-row v-else-if="elementType === VariableType.Bill"
                  :el="slotProps.item.bill" :bill-type="slotProps.item.billRowType" />
                <counterparty-row v-else-if="elementType === VariableType.Counterparty"
                  :el="slotProps.item" />
                <simple-element
                  v-else
                  :element="slotProps.item"
                  :elementType="elementType"
                  :filter_string="objectFilter ? filter_value.stringFilter : filter_value"
                />
              </slot>
            </template>
          </variable-list>
        </div>
      </div>

      <div
        v-if="!loading && !displayedDataLength && load_offset"
        class="simple-selector__empty"
        :class="{ 'simple-selector__empty--hint': showCreateHint }"
      >
        {{ emptyText }}
      </div>
      <div
        v-if="!loading && !displayedDataLength && load_offset && hasFilterText"
        class="simple-selector__empty-sub"
      >
        {{ noMatchesSubText }}
      </div>

      <div v-if="!load_offset" class="text-center q-my-md">
        <loading-skeleton v-if="!last_load_empty && loading" />
      </div>
      <template v-slot:loading>
        <div v-if="load_offset" class="text-center q-my-md">
          <loading-skeleton v-if="!last_load_empty && loading" />
        </div>
      </template>
    </q-infinite-scroll>
  </q-list>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from 'vue-facing-decorator';
import { reactive, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import _ from 'lodash';
import { buildHighlightSpans, tokenizeSearch } from '@/components/common/basic/search-highlight/searchHighlight';

import VariableList from '@/components/common/basic/VariableList.vue';
import SimpleElement from '@/components/common/basic/data-components/SimpleElement.vue';
import ParameterInlineDisplay from '@/components/multiedit/temporary/ParameterInlineDisplay.vue';
import BillRow from '@/components/multiedit/temporary/storageshared/BillRow.vue';
import CounterpartyRow from '@/components/multiedit/temporary/storageshared/CounterpartyRow.vue';
import { isAllSelected, toggleSelectAll } from '../composables/useSelectAll';
import MostaCheckbox from '@/components/common/basic/MostaCheckbox.vue';
import LoadingSkeleton from './LoadingSkeleton.vue';
import { hintsDiag } from '../utils/hints-diag';
import { AuxSelectorFilter, BillSearchedTypeDescriptor, VariableType,
  ParameterInstanceContainerType, V2FieldValueDisplayType, V2RecordAxisType } from '@/models/etc/types';
import { V2ParameterValueType } from '@/models/etc/parameter-types';
import type { ParameterInstance, ParameterInstanceContainer } from '@/models/interfaces/parameters/parameter-models';
import type { StorageItemSearched } from '@/models/interfaces/storage/storage-models';

@Component({
  name: 'SimpleSelector',
  components: { VariableList, SimpleElement, ParameterInlineDisplay, BillRow, CounterpartyRow, MostaCheckbox, LoadingSkeleton },
  expose: ['trackArrowUp', 'trackArrowDown', 'selectCurrent', 'selectByName', 'keyboardSelectionActive', 'removeItem', 'reload'],
})
export default class SimpleSelector extends Vue {
  VariableType = VariableType;

  @Prop({ default: '' }) focusElement!: '';
  @Prop({ default: 0 }) mHeight!: number;
  @Prop({ default: true }) allowMultiple!: boolean;
  @Prop({ default: false }) hideCheckboxes!: boolean;
  @Prop({ default: undefined }) dataToEntity?: (data: any) => any;
  @Prop({ default: undefined }) dataFromEntity?: (data: any) => any;
  @Prop({ default: undefined }) loadMore?: (
    filter_string: string | AuxSelectorFilter,
    load_count: number,
    load_offset: number,
  ) => any[];
  @Prop({ default: false }) hasDeps!: boolean;
  @Prop({ default: false }) isStoragesMode!: boolean;
  @Prop({ default: true }) showSelectAll!: boolean;
  // Список приходит одним батчем: нет offset-пагинации и дозагрузки.
  @Prop({ default: false }) singleLoad!: boolean;
  // Ключ локализации подсказки для пустого списка без фильтра.
  @Prop({ default: '' }) emptyHint!: string;
  // Ключ локализации второй строки под "Нет совпадений".
  @Prop({ default: '' }) noMatchesHint!: string;
  @Prop({ default: false }) full!: boolean;
  @Prop({ default: false }) objectFilter!: boolean;
  @Prop({ default: 0 }) storageForFilter!: number;
  @Prop({ default: VariableType.Entity }) elementType!: VariableType;

  divs: any[] = reactive([]);
  loading: boolean = false;

  @Prop({ default: [] as any[] }) modelValue!: any[];
  @Emit('update:modelValue') emitModelValue(arg: any[]) { return arg; }
  tmpModelData: any[] = reactive([]);
  @Emit('itemSelected') emitItemSelected(arg: any) { return arg; }
  @Emit('emptyClick') emitEmpty(arg: any) { return arg; }

  get model_mut() { return _.cloneDeep(this.modelValue); }

  @Watch('model_mut')
  model_watch(old: any, newv: any) {
    if (_.isEqual(old, newv)) return;
    this.data_list = reactive([...this.data_list]);
    this.tmpModelData = this.modelValue;
    if (this.dataToEntity !== undefined) {
      this.listModelData = this.tmpModelData.map(this.dataToEntity);
    }
  }

  listModelData: any[] = reactive([]);

  @Prop({ default: '' }) filter_value: string | AuxSelectorFilter = '';

  scrollTargetRef = ref(null);
  data_list: any[] = reactive([]);
  load_count = 40;
  load_offset = 0;
  last_load_empty = true;
  throttled: any;

  get isTableView(): boolean {
    return this.elementType === VariableType.ParameterInline;
  }

  get visibleDeps(): any[] {
    if (!this.hasDeps) return this.data_list;
    return this.data_list.filter((dep: any) => dep?.child?.length);
  }

  get depSearchTokens(): string[] {
    const rawFilter = this.objectFilter
      ? ((this.filter_value as AuxSelectorFilter)?.stringFilter || '')
      : ((this.filter_value as string) || '');
    return tokenizeSearch(rawFilter);
  }

  getDepTitleSpans(title: string): Array<{ text: string; style: string }> {
    return buildHighlightSpans(title || '', this.depSearchTokens);
  }

  // Пустой результат при непустом фильтре это "нет совпадений", а не "данных нет вообще".
  get hasFilterText(): boolean {
    const raw = this.objectFilter
      ? ((this.filter_value as AuxSelectorFilter)?.stringFilter || '')
      : ((this.filter_value as string) || '');
    return raw.trim().length > 0;
  }

  // Список пуст и фильтр не введён: в селекторах с созданием подсказываем создать первый элемент.
  get showCreateHint(): boolean {
    return !this.hasFilterText && !!this.emptyHint;
  }

  // Вторая строка под "Нет совпадений": с предложением создать элемент там, где это доступно.
  get noMatchesSubText(): string {
    return String(this.$t(this.noMatchesHint || 'change_query'));
  }

  get emptyText(): string {
    if (this.hasFilterText) return String(this.$t('no_matches'));
    if (this.emptyHint) return String(this.$t(this.emptyHint));
    return String(this.$t('no_data_found'));
  }

  get displayedDataLength(): number {
    return this.hasDeps ? this.visibleDeps.length : this.data_list.length;
  }

  created() {
    this.last_load_empty = !(
      this.loadMore !== undefined &&
      this.dataToEntity !== undefined &&
      this.dataFromEntity !== undefined
    );
    this.load_offset = 0;
    this.data_list = [];

    this.throttled = useDebounceFn(() => {
      if (this.loading) {
        // [hints][diag] фильтр сменился и список очищен, а новый запрос не ушёл
        hintsDiag('skipped-while-loading', {
          filter: this.objectFilter
            ? ((this.filter_value as AuxSelectorFilter)?.stringFilter || '')
            : ((this.filter_value as string) || ''),
          offset: this.load_offset,
        });
        return;
      }
      if (!this.$refs.infscr) return;
      this.last_load_empty = !(
        this.loadMore !== undefined &&
        this.dataToEntity !== undefined &&
        this.dataFromEntity !== undefined
      );
      this.data_list = reactive([]);
      this.load_offset = 0;
      // @ts-ignore
      this.$refs.infscr.stop();
      // @ts-ignore
      this.$refs.infscr.reset();
      // @ts-ignore
      this.$refs.infscr.setIndex(1);
      this.loadData();
    }, 10);

    this.tmpModelData = this.modelValue;
    if (this.dataToEntity !== undefined) {
      this.listModelData = this.tmpModelData.map(this.dataToEntity);
    }
  }

  get storageItemIsDisabled(): ((item: any, index: number) => boolean) | undefined {
    if (this.elementType === VariableType.ParameterInline && this.storageForFilter) {
      const storageId = this.storageForFilter;
      return (item: any) => {
        const count = item?.QuantitiesInStock?.find((q: { Id: number }) => q.Id === storageId)?.Count ?? 0;
        return count < 1;
      };
    }

    if (this.elementType === VariableType.Bill) {
      return (item: any) => {
        const bill = item?.bill;
        const isPayment = bill?.Descriptor === BillSearchedTypeDescriptor.Payment;
        return !!(isPayment && bill?.PaymentIsAlreadyAttached);
      };
    }

    return undefined;
  }

  // --- Position name model (для рендера StorageItems, elementType ParameterInline) ---

  getPositionNameModel(item: StorageItemSearched): ParameterInstanceContainer[] {
    const insts = item.NameParameters[0].Instance;
    let res: any[] =
      item.NameParameters.length === 1 &&
      item.NameParameters[0].Type === ParameterInstanceContainerType.SingleInstance &&
      insts
        ? insts.RecordFields
        : item.NameParameters;

    if (item.VendorCode) {
      const vcodeCell = {
        Type: ParameterInstanceContainerType.SingleInstance,
        Instance: {
          TemplateId: 999, Unremovable: true,
          ValueType: V2ParameterValueType.String,
          StringValue: '• ' + item.VendorCode,
          NumberValue: null, DateValue: null, BoolValue: null,
          ValueSet: null, SelectedValue: null,
          ValueDisplayType: V2FieldValueDisplayType.Inline,
          Name: this.$t('sku'), Id: 0, Round: 2,
          RecordAxisType: V2RecordAxisType.Horizontal,
          RecordFields: [],
        } as ParameterInstance,
        ListTitle: null,
      } as ParameterInstanceContainer;
      res = [vcodeCell, ...res];
    }

    const storageCell = {
      Type: ParameterInstanceContainerType.SingleInstance,
      Instance: {
        TemplateId: 998, Unremovable: true,
        ValueType: V2ParameterValueType.String,
        StringValue: item.QuantitiesInStock.map((q) => q.Name + ' (' + q.Count + ')').join(', '),
        NumberValue: null, DateValue: null, BoolValue: null,
        ValueSet: null, SelectedValue: null,
        ValueDisplayType: V2FieldValueDisplayType.Inline,
        Name: this.$t('warehouse'), Id: 0, Round: 2,
        RecordAxisType: V2RecordAxisType.Horizontal,
        RecordFields: [],
      } as ParameterInstance,
      ListTitle: null,
    } as ParameterInstanceContainer;
    res.push(storageCell);

    return res;
  }

  // --- Select all / Department selection ---

  getSelectedDepModel(d_index: number) {
    if (!this.hasDeps) return false;
    return this.divs[d_index] && (this.divs[d_index] as any).allSelected();
  }

  setSelectedDepModel(d_index: number) {
    if (!this.hasDeps) return false;
    if (!this.getSelectedDepModel(d_index)) {
      this.divs[d_index] && (this.divs[d_index] as any).selectAll();
    } else {
      this.divs[d_index] && (this.divs[d_index] as any).unselectAll();
    }
  }

  get getSelectedAll() {
    if (!this.hasDeps) {
      return isAllSelected(this.data_list, this.listModelData);
    }
    for (let i = 0; i < this.visibleDeps.length; i++) {
      if (this.divs[i] && !(this.divs[i] as any).allSelected()) {
        return false;
      }
    }
    return true;
  }

  setSelectedAll() {
    const sa = this.getSelectedAll;
    if (!this.hasDeps) {
      toggleSelectAll(this.$refs.simple_selector_list, sa);
    } else {
      for (let i = 0; i < this.divs.length; i++) {
        if (this.divs[i]) toggleSelectAll(this.divs[i], sa);
      }
    }
  }

  // --- Keyboard navigation ---

  trackArrowUp(arg: number, reset_index: boolean = false) {
    if (!this.hasDeps) {
      this.$refs.simple_selector_list && (this.$refs.simple_selector_list as any).trackArrowUp(arg);
    } else {
      const idx = this.findFirstDepWithChildren();
      this.divs[idx] && (this.divs[idx] as any).trackArrowUp(arg, reset_index);
    }
  }

  trackArrowDown(arg: number, reset_index: boolean = false) {
    if (!this.hasDeps) {
      this.$refs.simple_selector_list && (this.$refs.simple_selector_list as any).trackArrowDown(arg);
    } else {
      const idx = this.findFirstDepWithChildren();
      this.divs[idx] && (this.divs[idx] as any).trackArrowDown(arg, reset_index);
    }
  }

  selectCurrent(): boolean {
    if (!this.hasDeps) {
      return this.$refs.simple_selector_list ? (this.$refs.simple_selector_list as any).selectCurrent() : false;
    }
    const idx = this.findFirstDepWithChildren();
    return this.divs[idx] ? (this.divs[idx] as any).selectCurrent() : false;
  }

  // Выбор строки по точному имени, как по клику по ней. Индексы берём по data_list:
  // он же уходит в variable-list списком элементов.
  selectByName(name: string): boolean {
    if (this.hasDeps) return false;
    const list = this.$refs.simple_selector_list as any;
    if (!list) return false;
    const needle = name.trim().toLowerCase();
    const idx = this.data_list.findIndex(
      (el: any) => typeof el?.Name === 'string' && el.Name.trim().toLowerCase() === needle
    );
    if (idx < 0) return false;
    return !!list.selectElement?.(idx);
  }

  // По списку ходили стрелками, и подсвеченная строка выбрана осознанно
  keyboardSelectionActive(): boolean {
    if (this.hasDeps) return false;
    const list = this.$refs.simple_selector_list as any;
    return ((list?.keyboardSelectedIndex?.() ?? -1) as number) >= 0;
  }

  private findFirstDepWithChildren(): number {
    return this.visibleDeps.length ? 0 : -1;
  }

  // Убрать элемент из уже загруженной выдачи, без перезапроса страницы
  removeItem(item: any) {
    const idx = this.data_list.findIndex((el: any) => el === item || (el?.Id != null && el.Id === item?.Id));
    if (idx > -1) { this.data_list.splice(idx, 1); }
  }

  // Перезапрос выдачи с текущим фильтром. То же самое делает смена фильтра
  reload() {
    this.last_load_empty = !(
      this.loadMore !== undefined &&
      this.dataToEntity !== undefined &&
      this.dataFromEntity !== undefined
    );
    this.data_list = reactive([]);
    this.throttled();
  }

  // --- Watchers ---

  @Watch('focusElement')
  setFocus() { this.trackArrowDown(1); }

  get filterMutated() { return _.cloneDeep(this.filter_value); }

  @Watch('filterMutated', { deep: true })
  updateData(val: string, old_val: string) {
    if (val === old_val) return;
    if (old_val != null && _.isEqual(val, old_val)) return;
    this.last_load_empty = !(
      this.loadMore !== undefined &&
      this.dataToEntity !== undefined &&
      this.dataFromEntity !== undefined
    );
    this.data_list = reactive([]);
    this.throttled();
  }

  @Watch('listModelData', { deep: true })
  updateModel() {
    if (this.dataFromEntity !== undefined) {
      this.tmpModelData = this.listModelData.map(this.dataFromEntity);
      this.emitModelValue(this.tmpModelData);
    }
  }

  // --- Events ---

  onItemSelected(arg: any) { this.emitItemSelected(arg); }
  onEmptyClick(arg: any) { this.emitEmpty(arg); }

  // --- Data loading ---

  async loadData(index: number = 1, done: any = null) {
    if (this.loading) return;
    if (this.loadMore === undefined || this.last_load_empty) {
      this.loading = false;
      return;
    }

    this.loading = true;
    const last_load: any[] = await this.loadMore(this.filter_value, this.load_count, this.load_offset);
    this.data_list = [...this.data_list, ...(last_load ?? [])];
    this.load_offset = this.load_count * index;

    if (!last_load?.length || last_load?.length < this.load_count) {
      this.last_load_empty = true;
    }
    this.loading = false;

    if (this.$refs.infscr) {
      // @ts-ignore
      this.$refs.infscr.resume();
    }
    done && done();
  }
}
</script>

<style lang="scss" scoped>
.simple-selector__empty {
  padding: 7px 12px 0;
  text-align: center;
  color: var(--Text-color-text_main, #4D4D4D);
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.simple-selector__empty--hint {
  color: #979797;
}

.simple-selector__empty-sub {
  margin-top: 8px;
  padding: 0 12px;
  color: #979797;
  text-align: center;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}

.deps-header-row {
  min-height: 24px;
}

.deps-select-all-row--deps {
  // Только для deps: задаём стабильный отступ до блока департаментов.
  margin-bottom: 8px;
}

.select-all-row--storages {
  margin-bottom: 4px;
}

.select-all-check--storages {
  margin-left: 4px;
  margin-top: 4px;
  margin-right: 8px;
}

.deps-select-all-label {
  margin-left: 8px;
  color: var(--Text-color-text_main, #4D4D4D);
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  flex: 1 1 auto;
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.select-all-label--storages {
  margin-left: 0;
  margin-top: 4px;
}

.deps-header-check {
  margin-right: 8px;
  flex-shrink: 0;
  align-self: flex-start;
}

.deps-header-title {
  color: var(--Text-color-text_main, #4D4D4D);
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  flex: 1 1 auto;
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.deps-children-list {
  margin-left: 12px;
  overflow: hidden;
  min-width: 0;
}

.deps-block {
  margin-bottom: 16px;
  overflow: hidden;
  min-width: 0;

  &:last-child {
    margin-bottom: 0;
  }
}

</style>
